/**
 * MONSTER TOWN — HOW AN ERRAND ARRIVES. ERRAND CREW.
 *
 * Brief §3: *"Errands arrive naturally: a neighbour flags you down, a note
 * appears in your mailbox in the morning, the zoo needs help at feeding time."*
 * `ErrandDef.offer` is the data (`from`, `fromHour`, `toHour`, `afterDay`,
 * `requires`) and `errands.isOfferable()` reads the clock. This file is the
 * thing that actually walks up to the kid.
 *
 * ===========================================================================
 *  AN ARRIVAL IS NOT A DECISION
 * ===========================================================================
 * There is no yes/no card and no accept button, on purpose. A yes/no is a
 * question, a question a pre-reader cannot read is a wall, and "no" would have
 * to mean something — which is the first step toward an errand that can be
 * lost. So an errand simply ARRIVES: a monster waves, a picture flies onto the
 * deck, and it waits there forever. If the kid does not want it on screen they
 * tuck the deck away with one tap and nothing at all is lost (`errand:cardHidden`).
 *
 * ===========================================================================
 *  IT MUST NEVER FEEL LIKE BEING NAGGED
 * ===========================================================================
 *  - at most `MAX_RUNNING` errands at once, so the deck can never become a
 *    to-do list;
 *  - `GAP_S` between arrivals, and `SETTLE_S` of quiet after a load before the
 *    first one, so a kid is never ambushed the moment their town appears;
 *  - nothing arrives during the first-run tutorial, inside a panel, in the
 *    monster creator, in photo mode, or while VISITING somebody else's town;
 *  - nothing arrives that the kid cannot do (`canAnswer`), so a request is
 *    never an instruction to build a thing they have not unlocked.
 *
 * Under an automated browser arrivals are OFF unless a tool asks for them, for
 * exactly the reason the monster creator and the first-run tutorial are off:
 * every screenshot and perf run opens a fresh profile, and an errand card
 * sliding in would be in all 22 named angles. `window.__errands.auto(true)`.
 */

import { bus } from '../core/events'
import { engine } from '../core/engine'
import { save } from '../core/save'
import { world } from '../core/scene'
import { errands, type ErrandDef } from '../core/errands'
import type { NpcId, SceneId, Vec3 } from '../core/types'
import { app } from '../app/context'
import { canAnswer } from './requests'
import './events'

/** More cards than this is a to-do list, not a game. */
const MAX_RUNNING = 3
/** Seconds between two arrivals, whatever their source. */
const GAP_S = 30
/** Quiet after a town loads before anything is allowed to walk up. */
const SETTLE_S = 10
/** How often the "is anyone about to flag me down" check runs. Cheap. */
const CHECK_EVERY_S = 2.5
/** How long the neighbour who flagged you down stays scripted. */
const WAVE_S = 3

let quietFor = SETTLE_S
let checkIn = CHECK_EVERY_S
let busyWithTutorial = false
let auto = typeof navigator === 'undefined' || navigator.webdriver !== true
let wired = false
/** Every errand this session has already put in front of the kid. */
const announced = new Set<string>()

export function setAutoOffers(on: boolean): void {
  auto = on
}

export function autoOffersOn(): boolean {
  return auto
}

/** Nothing may arrive while any of these is true. */
function blocked(): boolean {
  if (!auto) return true
  if (busyWithTutorial) return true
  if (save.visiting) return true
  if (quietFor > 0) return true
  if (errands.active().length >= MAX_RUNNING) return true
  const phase = app.phase
  if (phase === 'monsterCreator' || phase === 'photo' || phase === 'slotPick' || phase === 'boot') {
    return true
  }
  return false
}

/** Offerable, doable, and not already shown this session. */
function candidates(from: string): ErrandDef[] {
  return errands
    .offerable()
    .filter((d) => (d.offer?.from ?? 'neighbour') === from)
    .filter((d) => !announced.has(d.id))
    .filter((d) => d.steps.every((s) => canAnswer(s)))
}

/**
 * THE ARRIVAL ITSELF. One place, so a mailbox note and a neighbour's wave can
 * never drift into two different experiences.
 */
function arrive(def: ErrandDef, from: string): void {
  announced.add(def.id)
  quietFor = GAP_S
  bus.emit('errand:offered', { id: def.id, giver: def.giver, from })
  errands.grant(def.id)

  /*
   * THE CONVERSATION ALREADY HAPPENED.
   *
   * A `talk` step is an ACT, so core deliberately never auto-satisfies one —
   * and until the NPC crew ships a real "tap the neighbour and they say a
   * thing", every errand that opens with `talk` would stall on step 0 for ever.
   * That is not a waiting errand, it is a stuck one.
   *
   * But the giver DID just walk over and ask, on screen, with a wave and a
   * sound. Reporting that through the one sanctioned wire is not a shortcut, it
   * is the truth about what the kid just watched. Narrow on purpose: only the
   * errand being handed over, and only when its current step is a talk to the
   * giver themselves. An errand that says "now go and find the zookeeper" is
   * untouched, and stays the NPC crew's to close.
   */
  const run = errands.run(def.id)
  const step = run === null ? undefined : def.steps[run.step]
  if (step !== undefined && step.kind === 'talk' && step.npc === def.giver) {
    bus.emit('errand:observe', { kind: 'talk', what: step.npc })
  }

  save.request(`errand:offered:${def.id}`)
}

/**
 * A NEIGHBOUR FLAGS YOU DOWN. Uses the NPC crew's own published wire
 * (`npc:requestGuide` -> `npc:guideReady`), which replies synchronously and
 * spawns somebody if the whole roster is busy — so this cannot silently do
 * nothing. If the NPC crew is not installed at all the errand still arrives,
 * just without the monster; the card is the promise, the wave is the flourish.
 */
function flagDown(def: ErrandDef, sceneId: SceneId, at: Vec3): void {
  let who: NpcId | null = null
  const off = bus.on('npc:guideReady', (e) => {
    who = e.npcId
  })
  bus.emit('npc:requestGuide', { sceneId, at })
  off()

  if (who !== null) {
    const npcId: NpcId = who
    bus.emit('npc:emote', { npcId, action: 'wave', seconds: WAVE_S })
    bus.emit('audio:cue', { cue: 'npc_hello' })
    window.setTimeout(() => bus.emit('npc:dismiss', { npcId, happy: true }), WAVE_S * 1000)
  }
  arrive(def, 'neighbour')
}

/** Somewhere beside the kid, on the ground they are standing on. */
function besidePlayer(): Vec3 {
  const p = app.playerPos
  return { x: p.x + 2.4, y: 0, z: p.z + 1.2 }
}

function tryNeighbour(): void {
  if (blocked()) return
  const sceneId = world.activeSceneId
  if (sceneId === null) return
  const node = world.get(sceneId)
  // A neighbour flags you down OUTSIDE. Nobody appears through a kitchen wall.
  if (node === undefined || node.def.kind !== 'exterior') return
  const def = candidates('neighbour')[0]
  if (def === undefined) return
  flagDown(def, sceneId, besidePlayer())
}

/** A NOTE IN THE MAILBOX, in the morning. A picture and a paper rustle. */
function tryMailbox(): void {
  if (blocked()) return
  const def = candidates('mailbox')[0]
  if (def === undefined) return
  bus.emit('audio:cue', { cue: 'paper' })
  bus.emit('ui:toast', { icon: 'door', ms: 2600 })
  arrive(def, 'mailbox')
}

/** The venue asks for help the moment you walk into it. */
function tryVenue(): void {
  if (blocked()) return
  const def = candidates('venue')[0]
  if (def === undefined) return
  arrive(def, 'venue')
}

/** Errands the game opens with. Offered once the town is on screen. */
function tryStart(): void {
  if (blocked()) return
  const def = candidates('start')[0]
  if (def === undefined) return
  arrive(def, 'start')
}

/**
 * QA/debug: put a specific errand in front of the kid right now — down the SAME
 * road it would have taken on its own, so a tool is testing the real arrival
 * and not a shortcut past it.
 */
export function offerNow(id: string): boolean {
  const def = errands.offerable().find((d) => d.id === id)
  if (def === undefined) return false
  quietFor = 0
  const from = def.offer?.from ?? 'neighbour'
  const sceneId = world.activeSceneId
  const node = sceneId === null ? undefined : world.get(sceneId)
  if (from === 'neighbour' && sceneId !== null && node?.def.kind === 'exterior') {
    flagDown(def, sceneId, besidePlayer())
    return true
  }
  if (from === 'mailbox') {
    bus.emit('audio:cue', { cue: 'paper' })
    bus.emit('ui:toast', { icon: 'door', ms: 2600 })
  }
  arrive(def, from)
  return true
}

/** Test surface: what could arrive right now, and why nothing is. */
export function offerReport(): {
  blocked: boolean
  auto: boolean
  quietFor: number
  running: number
  offerable: string[]
  doable: string[]
} {
  const offerable = errands.offerable()
  return {
    blocked: blocked(),
    auto,
    quietFor: Math.round(quietFor * 10) / 10,
    running: errands.active().length,
    offerable: offerable.map((d) => d.id).sort(),
    doable: offerable
      .filter((d) => d.steps.every((s) => canAnswer(s)))
      .map((d) => d.id)
      .sort(),
  }
}

export function installOffers(): void {
  if (wired) return
  wired = true

  /**
   * THE FIRST RUN OWNS THE SCREEN — WHILE IT IS BEING FOLLOWED, AND NO LONGER.
   *
   * This used to be `tutorial:started` -> true, `tutorial:finished` -> false.
   * `finished` fires only when all five beats are done, and the first run waits
   * FOR EVER by design (LAW #2) — so a three-year-old who stalled on beat four
   * got no errand in that session or in any future one, because the beat is
   * saved per slot. Measured: `offers.blocked = true` with three errands
   * offerable and waiting, after 45 s of ordinary play.
   *
   * `tutorial:claim` is the bounded half of the same signal (see the note in
   * `src/ui/tutorial.ts`): it goes false when the paw is torn down for ANY
   * reason, and also after ~30 s on one beat with no progress. The paw stays up
   * and keeps waiting; the game stops being switched off behind it.
   */
  bus.on('tutorial:claim', (e) => {
    busyWithTutorial = e.claimed
    if (!e.claimed) quietFor = Math.max(quietFor, SETTLE_S)
  })

  bus.on('save:loaded', () => {
    announced.clear()
    quietFor = SETTLE_S
    // Anything already running was announced in a previous session; it must not
    // be announced again just because the town reloaded.
    for (const r of errands.active()) announced.add(r.id)
  })

  // Morning post.
  bus.on('time:dayStarted', () => {
    quietFor = Math.min(quietFor, 2)
    window.setTimeout(tryMailbox, 1200)
  })

  bus.on('scene:entered', (e) => {
    const node = world.get(e.scene)
    if (node?.def.kind === 'interior') window.setTimeout(tryVenue, 900)
  })

  engine.addSystem({
    id: 'errands.offers',
    order: 45,
    fixed: (dt) => {
      if (quietFor > 0) quietFor = Math.max(0, quietFor - dt)
      checkIn -= dt
      if (checkIn > 0) return
      checkIn = CHECK_EVERY_S
      tryStart()
      tryNeighbour()
    },
  })
}
